/**
 * Filtres / tri / KPI du Parc Matériels V2 — présentation pure.
 * Aucune règle métier, aucun appel API.
 */
import {
  DIAGNOSTIC_LABELS,
  diagnosticLabel,
  isATraiter,
  typeLabel,
} from './materielDisplayMaps.js';

/** @typedef {'all'|'affectes'|'en_stock'|'a_traiter'|'printers'|''} ChipId */
/** @typedef {'activity_desc'|'sn_asc'|'pdv_asc'|'created_desc'} SortKey */

export const DEFAULT_PARC_FILTERS = Object.freeze({
  query: '',
  lifecycle: '',
  type: '',
  diagnostic: '',
  chip: 'all',
  sort: /** @type {SortKey} */ ('activity_desc'),
});

export const SORT_OPTIONS = [
  { value: 'activity_desc', label: 'Activité cloud' },
  { value: 'sn_asc', label: 'N° série' },
  { value: 'pdv_asc', label: 'Point de vente' },
  { value: 'created_desc', label: 'Date d’ajout' },
];

export const CHIP_DEFS = [
  { id: 'all', label: 'Tous' },
  { id: 'affectes', label: 'Affectés' },
  { id: 'en_stock', label: 'En stock' },
  { id: 'a_traiter', label: 'À traiter' },
  { id: 'printers', label: 'Imprimantes' },
];

const PRINTER_TYPES = new Set(['PRINTER_CLOUD', 'PRINTER_LOCAL']);

/**
 * @param {object[]} items
 */
export function computeParcKpis(items) {
  const list = Array.isArray(items) ? items : [];
  let affectes = 0;
  let enStock = 0;
  let aTraiter = 0;
  for (const item of list) {
    if (item?.status === 'AFFECTE') affectes += 1;
    if (item?.status === 'EN_STOCK') enStock += 1;
    if (isATraiter(item)) aTraiter += 1;
  }
  return {
    total: list.length,
    affectes,
    enStock,
    aTraiter,
  };
}

/**
 * @param {object} item
 * @param {string} query
 */
export function matchesParcSearch(item, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const blob = [
    item?.commercant?.nom,
    item?.serialNumber,
    item?.cloudSn,
    item?.model,
    item?.label,
    typeLabel(item?.type),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes(q);
}

/**
 * @param {object} item
 * @param {typeof DEFAULT_PARC_FILTERS} filters
 */
export function matchesParcFilters(item, filters) {
  if (!item) return false;
  const f = filters || DEFAULT_PARC_FILTERS;

  if (!matchesParcSearch(item, f.query)) return false;

  if (f.chip === 'a_traiter') {
    if (!isATraiter(item)) return false;
  } else if (f.chip === 'affectes') {
    if (item.status !== 'AFFECTE') return false;
  } else if (f.chip === 'en_stock') {
    if (item.status !== 'EN_STOCK') return false;
  } else if (f.chip === 'printers') {
    if (!PRINTER_TYPES.has(item.type)) return false;
  }

  if (f.lifecycle && item.status !== f.lifecycle) return false;

  if (f.type) {
    if (f.type === 'PRINTERS') {
      if (!PRINTER_TYPES.has(item.type)) return false;
    } else if (item.type !== f.type) {
      return false;
    }
  }

  if (f.diagnostic) {
    // Diagnostic pertinent uniquement pour les Affectés
    if (item.status !== 'AFFECTE') return false;
    const diag = diagnosticLabel(item);
    const wanted =
      f.diagnostic === 'OK'
        ? DIAGNOSTIC_LABELS.OK
        : f.diagnostic === 'ERROR'
          ? DIAGNOSTIC_LABELS.ERROR
          : DIAGNOSTIC_LABELS.CHECK;
    if (diag !== wanted) return false;
  }

  return true;
}

/**
 * @param {string|Date|null|undefined} a
 * @param {string|Date|null|undefined} b
 */
function timeValue(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Activité cloud : lastJournalAt → updatedAt → createdAt
 * @param {object} item
 */
export function activityTimestamp(item) {
  return (
    timeValue(item?.health?.lastJournalAt) ||
    timeValue(item?.updatedAt) ||
    timeValue(item?.createdAt)
  );
}

/**
 * @param {object[]} items
 * @param {SortKey} sort
 */
export function sortParcItems(items, sort = 'activity_desc') {
  const list = Array.isArray(items) ? [...items] : [];
  const key = sort || 'activity_desc';

  list.sort((a, b) => {
    if (key === 'sn_asc') {
      const sa = String(a?.serialNumber || '').toLowerCase();
      const sb = String(b?.serialNumber || '').toLowerCase();
      return sa.localeCompare(sb, 'fr');
    }
    if (key === 'pdv_asc') {
      const pa = String(a?.commercant?.nom || '').toLowerCase();
      const pb = String(b?.commercant?.nom || '').toLowerCase();
      // Non affectés en bas
      if (!pa && pb) return 1;
      if (pa && !pb) return -1;
      return pa.localeCompare(pb, 'fr');
    }
    if (key === 'created_desc') {
      return timeValue(b?.createdAt) - timeValue(a?.createdAt);
    }
    // activity_desc (défaut)
    return activityTimestamp(b) - activityTimestamp(a);
  });

  return list;
}

/**
 * @param {object[]} items
 * @param {typeof DEFAULT_PARC_FILTERS} filters
 */
export function filterAndSortParc(items, filters) {
  const f = filters || DEFAULT_PARC_FILTERS;
  const filtered = (Array.isArray(items) ? items : []).filter((item) =>
    matchesParcFilters(item, f)
  );
  return sortParcItems(filtered, f.sort);
}

/**
 * État filtre après clic KPI.
 * @param {'total'|'affectes'|'en_stock'|'a_traiter'} kpi
 * @param {typeof DEFAULT_PARC_FILTERS} current
 */
export function filtersFromKpi(kpi, current = DEFAULT_PARC_FILTERS) {
  const base = {
    ...current,
    query: current?.query || '',
    type: '',
    diagnostic: '',
    sort: current?.sort || 'activity_desc',
  };
  if (kpi === 'affectes') {
    return { ...base, chip: 'affectes', lifecycle: 'AFFECTE' };
  }
  if (kpi === 'en_stock') {
    return { ...base, chip: 'en_stock', lifecycle: 'EN_STOCK' };
  }
  if (kpi === 'a_traiter') {
    return { ...base, chip: 'a_traiter', lifecycle: '' };
  }
  return { ...base, chip: 'all', lifecycle: '' };
}

/**
 * État filtre après clic chip.
 * @param {ChipId} chipId
 * @param {typeof DEFAULT_PARC_FILTERS} current
 */
export function filtersFromChip(chipId, current = DEFAULT_PARC_FILTERS) {
  const base = {
    ...current,
    query: current?.query || '',
    diagnostic: '',
    type: chipId === 'printers' ? 'PRINTERS' : '',
    sort: current?.sort || 'activity_desc',
  };
  if (chipId === 'affectes') {
    return { ...base, chip: 'affectes', lifecycle: 'AFFECTE', type: '' };
  }
  if (chipId === 'en_stock') {
    return { ...base, chip: 'en_stock', lifecycle: 'EN_STOCK', type: '' };
  }
  if (chipId === 'a_traiter') {
    return { ...base, chip: 'a_traiter', lifecycle: '', type: '' };
  }
  if (chipId === 'printers') {
    return { ...base, chip: 'printers', lifecycle: '', type: 'PRINTERS' };
  }
  return { ...base, chip: 'all', lifecycle: '', type: '' };
}

export function resetParcFilters(sort = 'activity_desc') {
  return { ...DEFAULT_PARC_FILTERS, sort };
}

/** Seuil API actuel — bandeau honnête, pas de pagination client. */
export const API_LIST_SOFT_LIMIT = 500;
